<template>
  <div class="locations">
    <div :class="['search-input', {'in-focus': inFocus}]" @click="focusInput">
      <img class="search-input__icon" src="/icons/icomoon/icons-svg/icon-location.svg" width="24" height="24" alt="" aria-hidden="true">
      <div class="search-input__container">
        <div class="search-input__label">
          {{ t('Destination')}}
        </div>
        <div class="search-input__body">
          <input
              v-model="model"
              :placeholder="t('Enter a destination or property')"
              ref="inputRef"
              class="search-input__input search-input__destination-input"
              type="text"
              @input="inputHandler"
              @focus="focusHandler"
              @blur="inFocus = false"
          >
        </div>
      </div>
    </div>
    <div v-if="isOpen" class="locations-list context-menu-dropdown custom-scrollbar">
      <ul v-if="!isLoading && hasResults">
        <li
            :class="['location', { 'location--external': location.source !== 'internal' }]"
            v-for="location in locations.cities"
            :key="'city-' + location.id"
            @click="selectItem(location)">
          <!-- Internal cities: solid location pin -->
          <img v-if="location.source === 'internal'" class="search-input__icon" src="/icons/icomoon/icons-svg/icon-location.svg" width="24" height="24" alt="" aria-hidden="true">
          <!-- External cities: globe icon to indicate external source -->
          <svg v-else class="search-input__icon location__icon location__icon--external" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
          </svg>
          <div>
            <div class="location__name">{{ location.name }}</div>
            <div class="location__details">
              {{ [location.state_name || location.state || location.region, location.country_name || location.country].filter(Boolean).join(', ') }}
            </div>
          </div>
        </li>
        <li
            class="location"
            v-for="location in locations.hotels"
            :key="'hotel-' + location.id"
            @click="selectHotelItem(location)">
          <img class="search-input__icon" src="/icons/icomoon/icons-svg/icon-location.svg" width="24" height="24" alt="" aria-hidden="true">
          <div>
            <div class="location__name">{{ location.name }}</div>
            <div class="location__details">
              {{ [location.geo_place?.state_name || location.geo_place?.state, location.geo_place?.country_name || location.geo_place?.country].filter(Boolean).join(', ') }}
            </div>
          </div>
        </li>

        <li
            class="location"
            v-for="location in locations.activities"
            :key="'activity-' + location.id"
            @click="selectActivityItem(location)">
          <img class="search-input__icon" src="/icons/icomoon/icons-svg/icon-location.svg" width="24" height="24" alt="" aria-hidden="true">
          <div>
            <div class="location__name">{{ location.name }}</div>
            <div class="location__details">
              {{ [location.geo_place?.state_name || location.geo_place?.state, location.geo_place?.country_name || location.geo_place?.country].filter(Boolean).join(', ') }}
            </div>
          </div>
        </li>

        <li
            class="location location--poi"
            v-for="location in locations.pois"
            :key="'poi-' + location.id"
            @click="selectPoiItem(location)">
          <svg class="search-input__icon location__icon location__icon--poi" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
          </svg>
          <div>
            <div class="location__name">{{ location.name }}</div>
            <div class="location__details">{{ location.label }}</div>
          </div>
        </li>

        <li
            class="location location--neighborhood"
            v-for="location in locations.neighborhoods"
            :key="'neighborhood-' + location.id"
            @click="selectPoiItem(location)">
          <svg class="search-input__icon location__icon location__icon--neighborhood" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
          </svg>
          <div>
            <div class="location__name">{{ location.name }}</div>
            <div class="location__details">{{ location.label }}</div>
          </div>
        </li>

        <li
            class="location location--address"
            v-for="location in locations.addresses"
            :key="'address-' + location.id"
            @click="selectPoiItem(location)">
          <svg class="search-input__icon location__icon location__icon--address" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          <div>
            <div class="location__name">{{ location.name }}</div>
            <div class="location__details">{{ location.label }}</div>
          </div>
        </li>
      </ul>
      <LocationsPlaceholder v-else/>
    </div>
  </div>

</template>

<script setup lang="ts">
import { useSearchStore } from '~/store/search'
import { defineAsyncComponent } from 'vue';

const LocationsPlaceholder = defineAsyncComponent(() => import('~/components/SearchPanel/LocationsPlaceholder.vue'));
const { t } = useI18n();
const route = useRoute()
const props = defineProps({
  domain: {
    type: String,
    default: 'hotels',
  }
})

const inFocus = ref(false);
//searchStore
const searchStore = useSearchStore()

//emits
const emit = defineEmits(['update', 'searchActivity', 'searchHotel'])


//mockup data
const model = ref('')
const locations = ref(null as any)

//data
const isOpen = ref(false)
const isLoading = ref(true)
const inputRef = ref(null as any)

// computed
const hasResults = computed(() => {
  if (!locations.value) return false
  return (
    locations.value.cities?.length ||
    locations.value.hotels?.length ||
    locations.value.activities?.length ||
    locations.value.pois?.length ||
    locations.value.neighborhoods?.length ||
    locations.value.addresses?.length
  )
})

onMounted(async () => {
  await nextTick(() => {
    const citySlug = route.params.city || null;
    setDefaultCity(citySlug);
  });
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleKeyDown)
});


onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeyDown);
})

function handleKeyDown(e: KeyboardEvent) {
  if (e.key === 'Escape' && document.activeElement === inputRef.value) {
    isOpen.value = false;
  }
}
function handleClickOutside(e: any) {
  if (!e.target.classList.contains('locations')) {
    if (e.target.classList.contains('search-input__destination-input') && model?.value?.length > 2) {
      isOpen.value = !isOpen.value
      return
    }
    isOpen.value = false
  }
}


watch(
    () => route.params.city,
    async (newCitySlug) => {
      if (newCitySlug) {
        await setDefaultCity(newCitySlug);
      }
    }
);


//methods
async function setDefaultCity(slug: any) {
  if (!slug) return

  // Preserve POI name if a POI was selected
  const poiName = searchStore.selectedCity?.poiName
  const poiLabel = searchStore.selectedLocation?.label

  try {
    const resp = await searchStore.getCity(slug);
    locations.value = {
      cities: [resp]
    };

    // If this is a POI search, preserve the POI display name
    if (poiName || poiLabel) {
      model.value = poiLabel || poiName
      searchStore.selectedCity = resp
      searchStore.selectedLocation = resp
    } else {
      selectItem(resp);
    }
    isLoading.value = false;
  } catch (error) {
    // If city not found but we have a POI name, keep showing it
    if (poiName || poiLabel) {
      model.value = poiLabel || poiName
    }
    console.error('Error fetching city:', error);
  }
}

const inputHandler = debounce(async (_event: Event) => {
  isLoading.value = true
  if (!model.value || model.value.length < 3) {
    isOpen.value = false
    return
  }

  isOpen.value = true

  const result = await searchStore.getLocations({
    fts: model.value,
    domain: props.domain,
    includePois: true,
    includeAddresses: true,
  })
  isLoading.value = false
  if (!result) {
    return
  }

  locations.value = result
}, 500)

const focusHandler = () => {
  inFocus.value = true
  if (locations.value) {
    isOpen.value = true
  }
}

const selectItem = (location: any) => {
  // External cities (without slug) need coordinate-based search like POIs
  if (location.source && location.source !== 'internal' && !location.slug) {
    setPoiModelValue(location)
    emit('update', {
      ...location,
      isPoiSearch: true,
      city_name: location.name, // Use the city name for lookup
    })
  } else {
    setModelValue(location)
    emit('update', location)
  }
  isOpen.value = false
}


const selectHotelItem = (location: any) => {
  setModelValue(location)
  emit('searchHotel', 'hotels')
  emit('update', location)
  isOpen.value = false
}

const selectActivityItem = (location: any) => {
  setModelValue(location)
  emit('searchActivity', 'experiences')
  emit('update', location)
  isOpen.value = false
}

const selectPoiItem = (location: any) => {
  // POIs/landmarks/addresses use lat/lon coordinates for map centering
  // and search nearby hotels in that area
  setPoiModelValue(location)
  emit('update', {
    ...location,
    // Flag to indicate this is a coordinate-based search
    isPoiSearch: true,
  })
  isOpen.value = false
}


const focusInput = () => {
  inputRef.value?.focus()
}

function setModelValue(location: any) {
  const { name, geo_place, country:locationCountry, state:locationState, state_name: locationStateName, country_name: locationCountryName } = location

  const { state_name, country_name } = geo_place || {}

  const region = state_name || locationStateName || locationState
  const country = country_name || locationCountryName || locationCountry
  model.value = [name, region, country].filter(Boolean).join(', ')
}

function setPoiModelValue(location: any) {
  // POI locations come from Pelias with a formatted label
  if (location.label) {
    model.value = location.label
  } else {
    const parts = [location.name]
    if (location.city_name) parts.push(location.city_name)
    if (location.region) parts.push(location.region)
    if (location.country_name) parts.push(location.country_name)
    model.value = parts.join(', ')
  }
}

</script>

<style scoped lang="scss">

.locations {
  position: relative;

  .locations-list {
    position: absolute;
    margin-top: 4px;
    background: var(--white);
    width: 100%;
    max-height: 431px;
    z-index: 1001;
  }
}

.location {
  padding: 9px 16px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;

  &:hover {
    background-color: var(--light-grey, #f5f5f5);
  }

  &__name {
    font-size: 16px;
    font-weight: 400;
    line-height: 150%;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__details {
    color: var(--dark-grey);
    font-size: 12px;
    font-weight: 400;
    line-height: 133.333%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 300px;
  }

  &__icon {
    flex-shrink: 0;

    &--external {
      color: var(--dark-grey);
    }

    &--poi {
      color: var(--blue);
    }

    &--neighborhood {
      color: #7b1fa2;
    }

    &--address {
      color: #e65100;
    }
  }

  &--external {
    opacity: 0.85;
  }
}
</style>
